import staticData from "@/public/data/tour/results.json";
import Flex from "@/shared/components/common/Flex";
import Typography from "@/shared/components/common/Typography";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { calculateStayNightDuration } from "@/shared/libs/date";
import { isUndefined } from "@/shared/libs/type-utils";
import type { TourAvailabilityItem } from "@/types/services/tour/apis/Availability";
import { CaretLeft } from "@phosphor-icons/react";
import Link from "next/link";

interface TourCardPriceAndReserveProps {
  searchParams: SearchParams;
  tour: TourAvailabilityItem;
  slug: string;
  searchId: string;
  nextPageUrl: string;
}

export default function TourCardPriceAndReserve({
  searchParams,
  tour,
  slug,
  searchId,
  nextPageUrl,
}: TourCardPriceAndReserveProps) {
  const {
    nightLabel,
    manLabel,
    currencyLabel,
    reserveLabel,
    hotelDetailLabel,
  } = staticData.body.resultCard;
  const { checkIn, checkOut, rooms } = searchParams;
  const { price, total_price, id, key } = tour;
  const isBooked = isUndefined(price) && isUndefined(total_price);

  return (
    <Flex.Item className="max-md:w-full">
      <Flex direction={"col"} alignItems={"center"} gap={"1"}>
        <Flex
          direction={{
            default: "row",
            md: "col",
          }}
          alignItems={"center"}
          justifyContent={{
            default: "between",
            md: "center",
          }}
          gap={{
            default: "2",
            md: "0",
          }}
          className="w-full max-md:py-2 max-md:bg-muted max-md:ring-1 max-md:ring-border max-md:rounded-full max-md:px-3"
        >
          {!isBooked && (
            <Flex.Item>
              <Flex gap={"1"} justifyContent={"between"} alignItems={"center"}>
                <Typography variant="body3" fontWeight="medium">
                  {calculateStayNightDuration(checkIn, checkOut)} {nightLabel} ,
                  {rooms} {manLabel}
                </Typography>
              </Flex>
            </Flex.Item>
          )}

          {!isBooked && (
            <Flex.Item>
              <Flex alignItems={"center"} gap={"1"}>
                <Flex.Item>
                  <Typography
                    variant="body1"
                    fontWeight="semibold"
                    className="text-primary text-lg md:text-xl"
                  >
                    {price?.toLocaleString()}
                  </Typography>
                </Flex.Item>
                <Flex.Item>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    className="text-primary md:text-base"
                  >
                    {currencyLabel}
                  </Typography>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          )}
        </Flex>

        {/* - * Reserve Button * - */}

        {useDeviceType() !== "mobile" && (
          <Flex.Item className="w-full max-md:w-auto">
            <Flex direction={"col"} alignItems={"center"} gap={"1"}>
              <Link href={nextPageUrl} className="max-md:w-full">
                <Flex
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={"1"}
                  className="max-md:w-full md:flex group min-w-36 rounded-full px-4 py-2 bg-primary hover:bg-primary-90 transition-[background] duration-100 ease-easeInOutCirc"
                >
                  <Typography
                    variant="body1"
                    className="text-background dark:text-foreground !font-medium"
                  >
                    {isBooked ? hotelDetailLabel : reserveLabel}
                  </Typography>
                  <CaretLeft
                    size={20}
                    weight="regular"
                    className="text-background dark:text-foreground group-hover:-translate-x-3 transition duration-100 ease-easeInOutCirc"
                  />
                </Flex>
              </Link>
            </Flex>
          </Flex.Item>
        )}
      </Flex>
    </Flex.Item>
  );
}
